import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  Chip,
  Avatar,
  Alert,
  Snackbar,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import {
  Group as GroupIcon,
  School as SchoolIcon,
  SportsSoccer as LeagueIcon,
  Business as ClubIcon,
  AccountBalance as AcademyIcon,
  Search as SearchIcon,
  PersonAdd as JoinIcon,
  Lock as PrivateIcon,
  Public as PublicIcon,
  Approval as ApprovalIcon
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { useFirebase } from '../contexts/FirebaseContext';
import logger from '../utils/logger';

interface Group {
  id: string;
  name: string;
  type: 'team' | 'school' | 'league' | 'club' | 'academy';
  memberCount: number;
  subscriptionTier: 'free' | 'plus' | 'pro';
  adminName: string;
  description?: string;
  settings: {
    allowSelfJoin: boolean;
    requireApproval: boolean;
  };
}

const GroupDiscovery: React.FC = () => {
  const { user } = useAuth();
  const { db } = useFirebase();
  const [groups, setGroups] = useState<Group[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [typeFilter, setTypeFilter] = useState<string>('all');
  const [sortBy, setSortBy] = useState<'name' | 'members'>('name');
  const [joinDialogOpen, setJoinDialogOpen] = useState(false);
  const [codeDialogOpen, setCodeDialogOpen] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState<Group | null>(null);
  const [joinMessage, setJoinMessage] = useState('');
  const [inviteCode, setInviteCode] = useState('');
  const [processing, setProcessing] = useState<string | null>(null);
  const [myGroups, setMyGroups] = useState<Set<string>>(new Set());
  // Ids of this user's memberships an admin deactivated; rejoining replaces them.
  const [inactiveMemberships, setInactiveMemberships] = useState<Set<string>>(new Set());
  const [lastDoc, setLastDoc] = useState<any>(null);
  const [hasMore, setHasMore] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [snackbar, setSnackbar] = useState<{open: boolean; message: string; severity: 'success' | 'error' | 'info'}>({
    open: false,
    message: '',
    severity: 'success'
  });
  const PAGE_LIMIT = 10;

  const showSnackbar = (message: string, severity: 'success' | 'error' | 'info' = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  useEffect(() => {
    loadAvailableGroups();
    loadMyGroups();
  }, []);

  const loadAvailableGroups = async (pageToken: any = null, limitNum: number = PAGE_LIMIT) => {
    pageToken ? setLoadingMore(true) : setLoading(true);
    setError(null);
    try {
      const { collection, query, where, getDocs, orderBy, startAfter, limit: limitFn } = await import('firebase/firestore');

      if (!db) {
        logger.error('Firestore not available');
        pageToken ? setLoadingMore(false) : setLoading(false);
        return;
      }

      // Query the teams collection only
      let querySnapshot;
      
      try {
        // Try query with status filter first
        const constraints: any[] = [
          where('status', '==', 'active'),
          limitFn(limitNum)
        ];

        if (pageToken) {
          constraints.push(startAfter(pageToken));
        }

        const teamsRef = collection(db, 'teams');
        const q = query(teamsRef, ...constraints);
        querySnapshot = await getDocs(q);
        
      } catch (indexError) {
        logger.warn('❌ Firestore index missing for status query, trying simple query:', indexError);
        // Fallback to simpler query without status filter
        const simpleConstraints: any[] = [limitFn(limitNum)];
        if (pageToken) {
          simpleConstraints.push(startAfter(pageToken));
        }
        const teamsRef = collection(db, 'teams');
        const fallbackQuery = query(teamsRef, ...simpleConstraints);
        querySnapshot = await getDocs(fallbackQuery);
      }
      
      const loadedGroups: Group[] = [];
      logger.log(`📊 Found ${querySnapshot.size} documents in teams collection`);

      querySnapshot.forEach((doc) => {
        const data = doc.data() as any;
        if (data.settings?.allowSelfJoin || !data.settings?.allowSelfJoin) {
          loadedGroups.push({
            id: doc.id,
            name: data.name,
            type: data.type,
            memberCount: data.memberCount || 0,
            subscriptionTier: data.subscriptionTier || 'free',
            adminName: data.adminName,
            description: data.description,
            settings: {
              allowSelfJoin: data.settings?.allowSelfJoin || false,
              requireApproval: data.settings?.requireApproval || true
            }
          });
        }
      });

      const lastVisible = querySnapshot.docs[querySnapshot.docs.length - 1] || null;
      setLastDoc(lastVisible);
      setHasMore(querySnapshot.docs.length === limitNum);

      logger.log('✅ Loaded available groups:', loadedGroups.length);

      if (pageToken) {
        setGroups(prev => [...prev, ...loadedGroups]);
      } else {
        setGroups(loadedGroups);
      }
    } catch (error) {
      logger.error('❌ Error loading groups:', error);
      setError('Failed to load groups');
    }
    pageToken ? setLoadingMore(false) : setLoading(false);
  };

  const loadMyGroups = async () => {
    try {
      const { collection, query, where, getDocs } = await import('firebase/firestore');

      if (!db || !user?.uid) return;

      const q = query(
        collection(db, 'teamMembers'),
        where('userId', '==', user.uid)
      );

      const querySnapshot = await getDocs(q);
      const groupIds = new Set<string>();
      const inactive = new Set<string>();

      querySnapshot.forEach((doc) => {
        const data = doc.data();
        if (data.isActive === false) inactive.add(doc.id);
        else groupIds.add(data.teamId);
      });

      setMyGroups(groupIds);
      setInactiveMemberships(inactive);
    } catch (error) {
      logger.error('❌ Error loading user groups:', error);
    }
  };

  const filteredGroups = groups
    .filter(group => {
      // Type filter
      if (typeFilter !== 'all' && group.type !== typeFilter) return false;

      // Search filter
      if (searchQuery.trim()) {
        const query = searchQuery.toLowerCase();
        const matchesName = group.name.toLowerCase().includes(query);
        const matchesAdmin = group.adminName.toLowerCase().includes(query);
        const matchesType = group.type.toLowerCase().includes(query);
        if (!matchesName && !matchesAdmin && !matchesType) return false;
      }

      return true;
    })
    .sort((a, b) => {
      if (sortBy === 'members') {
        return b.memberCount - a.memberCount;
      }
      return a.name.localeCompare(b.name);
    });

  const handleJoinRequest = async () => {
    if (!selectedGroup || !user) return;

    setProcessing(selectedGroup.id);
    try {
      const { collection, addDoc, serverTimestamp } = await import('firebase/firestore');

      if (!db) {
        logger.error('Firestore not available');
        return;
      }

      if (selectedGroup.settings.allowSelfJoin && !selectedGroup.settings.requireApproval) {
        // Direct join - add to teamMembers immediately
        const memberData = {
          teamId: selectedGroup.id,
          userId: user.uid,
          userEmail: user.email,
          userDisplayName: user.displayName || user.email,
          role: 'member',
          joinedAt: serverTimestamp(),
          addedBy: user.uid,
          joinMethod: 'self_join'
        };

        await addDoc(collection(db, 'teamMembers'), memberData);
        
        showSnackbar(`Successfully joined ${selectedGroup.name}!`);
        await loadMyGroups(); // Refresh user's groups
      } else {
        // Create join request
        const requestData = {
          userId: user.uid,
          userEmail: user.email || '',
          userDisplayName: user.displayName || user.email || '',
          groupId: selectedGroup.id,
          groupName: selectedGroup.name,
          groupType: selectedGroup.type,
          requestedAt: serverTimestamp(),
          status: 'pending',
          message: joinMessage.trim() || undefined
        };

        await addDoc(collection(db, 'joinRequests'), requestData);
        
        showSnackbar(`Join request sent to ${selectedGroup.name}! You'll be notified when it's processed.`);
      }

      setJoinDialogOpen(false);
      setSelectedGroup(null);
      setJoinMessage('');
    } catch (error) {
      logger.error('❌ Error joining group:', error);
      setError('Failed to join group');
    }
    setProcessing(null);
  };

  const handleJoinWithCode = async () => {
    if (!inviteCode.trim() || !user) return;

    setProcessing('code-join');
    try {
      const { doc, getDoc, deleteDoc, writeBatch, arrayUnion, serverTimestamp } = await import('firebase/firestore');

      if (!db) {
        logger.error('Firestore not available');
        return;
      }

      // Codes are looked up by exact id in inviteCodes/; team and organization
      // documents no longer carry them (any signed-in player can read those).
      const code = inviteCode.trim().toUpperCase();
      const invite = code && code.length <= 32 && !code.includes('/')
        ? await getDoc(doc(db, 'inviteCodes', code)) : null;
      const inv = invite?.exists() ? invite.data() : null;
      if (!inv || inv.active !== true || !['teams', 'organizations'].includes(inv.collection)) {
        setError('Invalid invite code');
        setProcessing(null);
        return;
      }

      const entityRef = doc(db, inv.collection, inv.entityId);
      const entity = await getDoc(entityRef);
      if (!entity.exists()) {
        setError('Invalid invite code');
        setProcessing(null);
        return;
      }
      const entityData = entity.data();
      // A suspended or archived organization can't be joined by code; older
      // organizations have no status and count as active.
      if (inv.collection === 'organizations' && (entityData.status ?? 'active') !== 'active') {
        setError('Invalid invite code');
        setProcessing(null);
        return;
      }
      if ((entityData.memberIds || []).includes(user.uid) || myGroups.has(inv.entityId)) {
        setError(`You are already a member of ${entityData.name}`);
        setProcessing(null);
        return;
      }

      // One batch: the join proof (checked by the rules against the code),
      // the membership the iOS app reads (memberIds) and the web membership
      // docs, whose ids and fields the rules fix.
      const membership = (teamId: string, joinMethod: string, viaTeamId?: string) => ({
        id: `${teamId}__${user.uid}`,
        teamId,
        userId: user.uid,
        userDisplayName: user.displayName || 'Player',
        role: 'member',
        joinedAt: serverTimestamp(),
        addedBy: user.uid,
        joinMethod,
        isActive: true,
        ...(viaTeamId ? { viaTeamId } : {}),
      });
      const isTeam = inv.collection === 'teams';
      const batch = writeBatch(db);
      batch.set(doc(entityRef, 'joins', user.uid), { code, joinedAt: serverTimestamp() });
      batch.update(entityRef, { memberIds: arrayUnion(user.uid) });
      batch.set(doc(db, 'teamMembers', `${inv.entityId}__${user.uid}`),
        membership(inv.entityId, isTeam ? 'team_invite_code' : 'org_invite_code'));
      const joinsOrganization = isTeam && entityData.organizationId && !myGroups.has(entityData.organizationId);
      if (joinsOrganization) {
        batch.set(doc(db, 'teamMembers', `${entityData.organizationId}__${user.uid}`),
          membership(entityData.organizationId, 'auto_org_join', inv.entityId));
      }
      // The rules only let a member create a membership, not reactivate one, so
      // a removed member's old record is deleted (owners may) before rejoining.
      const rejoined = [inv.entityId, ...(joinsOrganization ? [entityData.organizationId] : [])]
        .map((id) => `${id}__${user.uid}`)
        .filter((id) => inactiveMemberships.has(id));
      await Promise.all(rejoined.map((id) => deleteDoc(doc(db, 'teamMembers', id))));
      await batch.commit();

      showSnackbar(isTeam
        ? `Successfully joined ${entityData.name}!${joinsOrganization && entityData.organizationName ? ` You're also now a member of ${entityData.organizationName}.` : ''}`
        : `Successfully joined ${entityData.name} organization!`);

      setCodeDialogOpen(false);
      setInviteCode('');
      await Promise.all([loadMyGroups(), loadAvailableGroups()]);
    } catch (error) {
      logger.error('❌ Error joining with code:', error);
      setError('Failed to join with code');
    }
    setProcessing(null);
  };

  const openJoinDialog = (group: Group) => {
    setSelectedGroup(group);
    setJoinDialogOpen(true);
  };

  const getGroupIcon = (type: string) => {
    switch (type) {
      case 'school': return <SchoolIcon />;
      case 'league': return <LeagueIcon />;
      case 'club': return <ClubIcon />;
      case 'academy': return <AcademyIcon />;
      default: return <GroupIcon />;
    }
  };

  const getGroupTypeColor = (type: string) => {
    switch (type) {
      case 'team': return '#1976d2';
      case 'school': return '#9c27b0';
      case 'league': return '#2e7d32';
      case 'club': return '#f57c00';
      case 'academy': return '#0288d1';
      default: return '#666';
    }
  };

  const getTierColor = (tier: string) => {
    switch (tier) {
      case 'pro': return 'secondary';
      case 'plus': return 'primary';
      default: return 'default';
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h6" component="h3" fontWeight="bold">
          Discover Groups
        </Typography>
        <Button
          variant="outlined"
          onClick={() => setCodeDialogOpen(true)}
        >
          Join with Code
        </Button>
      </Box>

      {/* Search and Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              size="small"
              label="Search Groups"
              placeholder="Search by name, admin, or type..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />
              }}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel id="group-type-label">Group Type</InputLabel>
              <Select
                labelId="group-type-label"
                value={typeFilter}
                label="Group Type"
                onChange={(e) => setTypeFilter(e.target.value)}
              >
                <MenuItem value="all">All Types</MenuItem>
                <MenuItem value="team">Teams</MenuItem>
                <MenuItem value="school">Schools</MenuItem>
                <MenuItem value="league">Leagues</MenuItem>
                <MenuItem value="club">Clubs</MenuItem>
                <MenuItem value="academy">Academies</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel id="group-sort-label">Sort By</InputLabel>
              <Select
                labelId="group-sort-label"
                value={sortBy}
                label="Sort By"
                onChange={(e) => setSortBy(e.target.value as 'name' | 'members')}
              >
                <MenuItem value="name">Name</MenuItem>
                <MenuItem value="members">Most Members</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      ) : filteredGroups.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <GroupIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" component="p" color="text.secondary">
            No groups found
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Try adjusting your search criteria or join with an invite code.
          </Typography>
        </Paper>
      ) : (
        <>
          <Grid container spacing={3}>
            {filteredGroups.map((group) => (
              <Grid item xs={12} sm={6} md={4} key={group.id}>
                <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                  <CardContent sx={{ flex: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <Avatar
                        sx={{
                          bgcolor: getGroupTypeColor(group.type),
                          width: 48,
                          height: 48,
                          mr: 2
                        }}
                      >
                        {getGroupIcon(group.type)}
                      </Avatar>
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="h6" component="h4" fontWeight="bold">
                          {group.name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          by {group.adminName}
                        </Typography>
                      </Box>
                    </Box>

                    <Box sx={{ mb: 2 }}>
                      <Chip
                        label={group.type.toUpperCase()}
                        size="small"
                        sx={{
                          bgcolor: getGroupTypeColor(group.type) + '20',
                          color: getGroupTypeColor(group.type),
                          mr: 1
                        }}
                      />
                      <Chip
                        label={group.subscriptionTier.toUpperCase()}
                        color={getTierColor(group.subscriptionTier) as any}
                        size="small"
                      />
                    </Box>

                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      {group.memberCount} members
                    </Typography>

                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      {group.settings.allowSelfJoin ? (
                        <PublicIcon sx={{ fontSize: 16, mr: 1, color: 'success.main' }} />
                      ) : (
                        <PrivateIcon sx={{ fontSize: 16, mr: 1, color: 'warning.main' }} />
                      )}
                      <Typography variant="caption" color="text.secondary">
                        {group.settings.allowSelfJoin ? 'Open to join' : 'Invite only'}
                      </Typography>

                      {group.settings.requireApproval && (
                        <>
                          <ApprovalIcon sx={{ fontSize: 16, ml: 2, mr: 1, color: 'info.main' }} />
                          <Typography variant="caption" color="text.secondary">
                            Requires approval
                          </Typography>
                        </>
                      )}
                    </Box>

                    {group.description && (
                      <Typography variant="body2" sx={{ mb: 2 }}>
                        {group.description}
                      </Typography>
                    )}
                  </CardContent>

                  <Box sx={{ p: 2, pt: 0 }}>
                    {myGroups.has(group.id) ? (
                      <Button
                        fullWidth
                        variant="outlined"
                        disabled
                      >
                        Already Joined
                      </Button>
                    ) : group.settings.allowSelfJoin ? (
                      <Button
                        fullWidth
                        variant="contained"
                        startIcon={<JoinIcon />}
                        onClick={() => openJoinDialog(group)}
                        disabled={processing === group.id}
                      >
                        {processing === group.id ? 'Joining...' : 'Join Group'}
                      </Button>
                    ) : (
                      <Button
                        fullWidth
                        variant="outlined"
                        disabled
                      >
                        Invite Only
                      </Button>
                    )}
                  </Box>
                </Card>
              </Grid>
            ))}
          </Grid>
          {hasMore && (
            <Box sx={{ textAlign: 'center', mt: 3 }}>
              <Button
                variant="outlined"
                onClick={() => loadAvailableGroups(lastDoc)}
                disabled={loadingMore}
              >
                {loadingMore ? 'Loading...' : 'Load More'}
              </Button>
            </Box>
          )}
        </>
      )}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
      >
        <Alert
          onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* Join Request Dialog */}
      <Dialog open={joinDialogOpen} onClose={() => setJoinDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Join {selectedGroup?.name}</DialogTitle>
        <DialogContent>
          <Box mt={2}>
            {selectedGroup?.settings.requireApproval ? (
              <Alert severity="info" sx={{ mb: 2 }}>
                This group requires approval. Your request will be reviewed by the group admin.
              </Alert>
            ) : (
              <Alert severity="success" sx={{ mb: 2 }}>
                You can join this group immediately!
              </Alert>
            )}

            <TextField
              fullWidth
              multiline
              rows={3}
              label="Message to Group Admin (optional)"
              value={joinMessage}
              onChange={(e) => setJoinMessage(e.target.value)}
              placeholder="Introduce yourself or explain why you want to join..."
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setJoinDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleJoinRequest}
            variant="contained"
            disabled={processing !== null}
          >
            {processing ? 'Processing...' : selectedGroup?.settings.requireApproval ? 'Send Request' : 'Join Now'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Join with Code Dialog */}
      <Dialog open={codeDialogOpen} onClose={() => setCodeDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Join with Invite Code</DialogTitle>
        <DialogContent>
          <Box mt={2}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Enter the invite code provided by the group admin.
            </Typography>
            <TextField
              fullWidth
              label="Invite Code"
              value={inviteCode}
              onChange={(e) => setInviteCode(e.target.value.toUpperCase())}
              placeholder="Enter invite code..."
              inputProps={{ style: { textTransform: 'uppercase' } }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCodeDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleJoinWithCode}
            variant="contained"
            disabled={!inviteCode.trim() || processing === 'code-join'}
          >
            {processing === 'code-join' ? 'Joining...' : 'Join Group'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default GroupDiscovery;